import React, { useState, useCallback } from 'react'
import Cropper, { Area } from 'react-easy-crop'
import Modal from './Modal'
import Button from './Button'
import { getCroppedImg } from '../../utils/cropImage'
import { ZoomIn, ZoomOut } from 'lucide-react'

interface ImageCropModalProps {
  isOpen: boolean
  onClose: () => void
  imageSrc: string
  onCropComplete: (croppedFile: File) => void
  aspectRatio?: number
  title?: string
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1, // 1:1 square default
  title = 'Crop Image'
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const onCropChange = useCallback((location: { x: number; y: number }) => {
    setCrop(location)
  }, [])

  const onZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom)
  }, [])

  const onCropCompleteCallback = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  const handleApply = async () => {
    if (!croppedAreaPixels) return

    try {
      setIsProcessing(true)
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels)
      onCropComplete(croppedImage)
      // Reset state for next use
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setCroppedAreaPixels(null)
    } catch (error) {
      console.error('Error cropping image:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    // Reset state when closing
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size='lg'
      closeOnOverlayClick={false}
    >
      <div className='flex flex-col gap-4'>
        {/* Cropper Container */}
        <div className='relative h-80 bg-gray-900 rounded-lg overflow-hidden'>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            cropShape='rect'
            showGrid={true}
            style={{
              containerStyle: {
                borderRadius: '0.5rem'
              }
            }}
          />
        </div>

        {/* Zoom Controls */}
        <div className='flex items-center gap-3 px-2'>
          <ZoomOut className='h-4 w-4 text-gray-500' />
          <input
            type='range'
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className='flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-avi-primary'
          />
          <ZoomIn className='h-4 w-4 text-gray-500' />
        </div>

        {/* Help Text */}
        <p className='text-xs text-gray-500 text-center'>
          Drag to reposition. Use slider or scroll to zoom.
        </p>

        {/* Actions */}
        <div className='flex justify-end gap-3 pt-2 border-t border-gray-100'>
          <Button variant='outline' onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleApply}
            loading={isProcessing}
            disabled={isProcessing}
          >
            Apply Crop
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ImageCropModal
